/**
 * Informations sur les types de récompenses disponibles
 */

export const getRewardTypeInfo = (type) => {
  const rewardTypes = {
    CODE: {
      name: "Code de réduction",
      icon: "🎟️",
      description: "Codes de réduction pour les clients",
      complexity: "simple",
      needsWaterVolume: false,
    },
    EAU: {
      name: "Don d'eau",
      icon: "💧",
      description: "Volume d'eau donné à une association caritative",
      complexity: "medium",
      needsWaterVolume: true,
    },
    SLIDER: {
      name: "Slider",
      icon: "🎚️",
      description: "Récoompense pouvant être débloqué auprès d'un vendeur",
      complexity: "medium",
      needsWaterVolume: false,
    },
  };

  return (
    rewardTypes[type] || {
      name: "Type inconnu",
      icon: "❓",
      description: "Type de récompense non reconnu",
      complexity: "unknown",
      needsWaterVolume: false,
    }
  );
};

/**
 * Retourne les champs requis pour un type de récompense
 */
export const getRequiredFields = (type) => {
  const baseFields = ["rwName", "rwType", "rwCost", "rwPictureURL", "descriptions"];

  if (type === "EAU") {
    return [...baseFields, "rwWaterVolume"];
  }

  return baseFields;
};

/**
 * Valide les données d'une récompense
 */
export const validateRewardData = (type, data) => {
  const errors = [];
  const typeInfo = getRewardTypeInfo(type);

  // Validation des champs de base
  if (!data.rwName?.trim()) {
    errors.push("Le nom technique est obligatoire");
  }

  if (data.rwCost === "" || data.rwCost === null || data.rwCost === undefined) {
    errors.push("Le coût est obligatoire");
  } else if (Number(data.rwCost) < 0) {
    errors.push("Le coût doit être supérieur ou égal à 0");
  }

  if (!data.rwPictureURL?.trim()) {
    errors.push("L'image est obligatoire");
  }

  // Validation spécifique pour EAU
  if (typeInfo.needsWaterVolume) {
    if (data.rwWaterVolume === "" || data.rwWaterVolume === null || data.rwWaterVolume === undefined) {
      errors.push("Le volume d'eau est obligatoire pour ce type de récompense");
    } else if (Number(data.rwWaterVolume) < 0) {
      errors.push("Le volume d'eau doit être supérieur ou égal à 0");
    }
  }

  // Validation des descriptions
  if (!data.descriptions || data.descriptions.length === 0) {
    errors.push("Au moins une description est obligatoire");
  } else {
    data.descriptions.forEach((desc, index) => {
      if (!desc.name?.trim()) {
        errors.push(`Nom manquant pour la description ${index + 1}`);
      }
      if (!desc.description?.trim()) {
        errors.push(`Description manquante pour la description ${index + 1}`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Crée les données par défaut pour un type de récompense
 */
export const createDefaultRewardData = (type) => {
  const typeInfo = getRewardTypeInfo(type);

  const baseData = {
    rwType: type,
    rwName: "",
    rwCost: 100,
    rwAmount: 1.0,
    rwPictureURL: "",
    rwTitleColor: "",
    descriptions: [
      {
        language: "fr",
        name: "",
        description: "",
      },
    ],
  };

  if (typeInfo.needsWaterVolume) {
    baseData.rwWaterVolume = 100;
  }

  return baseData;
};

/**
 * Formate les données pour l'API
 */
export const formatRewardDataForAPI = (formData) => {
  const apiData = {
    rwName: formData.rwName,
    rwType: formData.rwType,
    rwCost: parseInt(formData.rwCost, 10),
    rwPictureURL: formData.rwPictureURL,
    descriptions: formData.descriptions,
  };

  if (formData.rwAmount !== null && formData.rwAmount !== undefined) {
    const amount = Number(formData.rwAmount);
    if (!Number.isNaN(amount)) {
      apiData.rwAmount = amount;
    }
  }

  if (formData.rwTitleColor) {
    apiData.rwTitleColor = formData.rwTitleColor;
  }

  if (formData.rwWaterVolume !== null && formData.rwWaterVolume !== undefined && formData.rwWaterVolume !== "") {
    const volume = parseInt(formData.rwWaterVolume, 10);
    if (!Number.isNaN(volume)) {
      apiData.rwWaterVolume = volume;
    }
  }

  return apiData;
};

/**
 * Valide une URL d'image
 */
export const validateImageURL = (url) => {
  if (!url) return false;

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Valide une couleur hexadécimale
 */
export const validateHexColor = (color) => {
  if (!color) return true; // Optionnel

  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexRegex.test(color);
};
